import type { Channel, Character } from '../../domain/schemas';
import { characterLayout } from '../../scene/characters';

/**
 * Shared character motion data (Phase 3B presets and Phase 3C templates). Pose angles are
 * degrees, clockwise on screen; offsets are fractions of the instance height (see
 * scene/characters.ts). A cycle is described by its values at the four quarter points
 * (phase 0, ¼, ½, ¾).
 */
export type Quarters = [number, number, number, number];
export type CycleShape = Partial<Record<Channel, Quarters>>;

export const r2 = (v: number): number => Math.round(v * 100) / 100;

const DEFAULT_LEGS = 0.36;

/** Limb swing adapted to leg length: longer legs swing less for the same stride (clamped). */
export function legSwing(base: number, character: Character | undefined): number {
  const legs = character?.proportions.legLength ?? DEFAULT_LEGS;
  return r2(Math.min(base * 1.4, Math.max(base * 0.6, base * Math.sqrt(DEFAULT_LEGS / legs))));
}

export const IDLE_SHAPE: CycleShape = {
  'pose.torsoY': [0, -0.008, 0, 0.004],
  'pose.head': [0, 2, 0, -2],
  'pose.armL': [3, 5, 3, 1],
  'pose.armR': [-3, -5, -3, -1],
};

/** Walk swing before leg-length adaptation (degrees). */
export const WALK_SWING = 26;
/** Run swing before leg-length adaptation (degrees). */
export const RUN_SWING = 44;

/** Walk cycle as used by the Phase 3B preset (legs lead at phase 0). */
export function walkShape(character: Character | undefined, swing = legSwing(WALK_SWING, character)): CycleShape {
  const leg = swing;
  const arm = r2(leg * 0.8);
  return {
    'pose.legL': [leg, 0, -leg, 0],
    'pose.legR': [-leg, 0, leg, 0],
    'pose.armL': [-arm, 0, arm, 0],
    'pose.armR': [arm, 0, -arm, 0],
    'pose.torsoY': [0.006, -0.01, 0.006, -0.01],
    'pose.torso': [2, 2, 2, 2],
  };
}

/** Run cycle as used by the Phase 3B preset. */
export function runShape(character: Character | undefined, swing = legSwing(RUN_SWING, character)): CycleShape {
  const leg = swing;
  const arm = r2(leg * 1.1);
  return {
    'pose.legL': [leg, 0, -leg, 0],
    'pose.legR': [-leg, 0, leg, 0],
    'pose.armL': [-arm, 0, arm, 0],
    'pose.armR': [arm, 0, -arm, 0],
    'pose.torsoY': [0.012, -0.02, 0.012, -0.02],
    'pose.torso': [8, 8, 8, 8],
    'pose.head': [-4, -4, -4, -4],
  };
}

/** Jump poses at crouch, take-off, peak and landing (Phase 3B preset). */
export const JUMP_POSES: CycleShape = {
  'pose.torsoY': [0.03, 0, 0, 0.01],
  'pose.armL': [30, 150, 160, 40],
  'pose.armR': [-30, -150, -160, -40],
  'pose.legL': [8, -6, -10, 4],
  'pose.legR': [-8, 6, 10, -4],
};

/** Leg length in scene pixels for an instance (hip to ankle). */
export function legPixels(character: Character | undefined, height: number): number {
  if (!character) return height * 0.36;
  return characterLayout(character.proportions).legH * height;
}

/**
 * Horizontal foot travel of one full locomotion cycle (two steps): each step moves the
 * foot from −swing to +swing, i.e. 2 · leg · sin(swing).
 */
export const travelPerCycle = (legPx: number, swingDeg: number): number => 4 * legPx * Math.sin((Math.abs(swingDeg) * Math.PI) / 180);
